import React, { useState } from "react";
import { Sidebar } from "../components/Sidebar";
import {
  Settings,
  Bell,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  MessageSquare,
  GitBranch,
  Code,
  FileText,
  MoreVertical,
  Filter,
  Menu,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { cn } from "../lib/utils";

interface Integration {
  id: string;
  name: string;
  description: string;
  category:
    | "ai-models"
    | "databases"
    | "monitoring"
    | "communication"
    | "development"
    | "security"
    | "analytics";
  status: "connected" | "disconnected" | "error" | "configuring";
  icon: React.ComponentType<any>;
  logo?: string; // Path to the logo file in public directory
  provider: string;
  lastSync?: Date;
  syncStatus?: "success" | "error" | "pending";
  features: string[];
  apiKey?: string;
  endpoint?: string;
  isConfigured: boolean;
  isActive: boolean;
  priority: "critical" | "high" | "medium" | "low";
}

const integrations: Integration[] = [
  // Communication & Notifications
  {
    id: "slack",
    name: "Slack",
    description: "Team notifications and alerts for test results and anomalies",
    category: "communication",
    status: "connected",
    icon: MessageSquare,
    logo: "/slack.svg",
    provider: "Slack",
    lastSync: new Date(Date.now() - 1 * 60 * 1000),
    syncStatus: "success",
    features: [
      "Test Alerts",
      "Team Notifications",
      "Status Updates",
      "Channel Integration",
    ],
    apiKey: "xoxb-...",
    endpoint: "https://hooks.slack.com/services/...",
    isConfigured: true,
    isActive: true,
    priority: "high",
  },
  {
    id: "teams",
    name: "Microsoft Teams",
    description: "Enterprise team collaboration and notifications",
    category: "communication",
    status: "connected",
    icon: MessageSquare,
    logo: "/teams.svg",
    provider: "Microsoft",
    lastSync: new Date(Date.now() - 5 * 60 * 1000),
    syncStatus: "success",
    features: [
      "Team Notifications",
      "Channel Messages",
      "Webhook Integration",
      "Enterprise SSO",
    ],
    apiKey: "https://...webhook.office.com/...",
    endpoint: "https://webhook.office.com/webhookb2/...",
    isConfigured: true,
    isActive: true,
    priority: "high",
  },

  // Development & CI/CD
  {
    id: "github",
    name: "GitHub",
    description: "Source code management, CI/CD, and issue tracking",
    category: "development",
    status: "connected",
    icon: GitBranch,
    logo: "/github.svg",
    provider: "GitHub",
    lastSync: new Date(Date.now() - 2 * 60 * 1000),
    syncStatus: "success",
    features: [
      "Code Repository",
      "CI/CD",
      "Issue Tracking",
      "Pull Requests",
      "Actions",
    ],
    apiKey: "ghp_...",
    endpoint: "https://api.github.com",
    isConfigured: true,
    isActive: true,
    priority: "critical",
  },
  {
    id: "gitlab",
    name: "GitLab",
    description: "Alternative source code management and DevOps platform",
    category: "development",
    status: "disconnected",
    icon: GitBranch,
    logo: "/gitlab.svg",
    provider: "GitLab",
    features: [
      "Code Repository",
      "CI/CD",
      "Issue Tracking",
      "Merge Requests",
      "Pipelines",
    ],
    isConfigured: false,
    isActive: false,
    priority: "high",
  },

  // Project Management
  {
    id: "jira",
    name: "Jira",
    description: "Project management and issue tracking for test automation",
    category: "development",
    status: "connected",
    icon: FileText,
    logo: "/jira.svg",
    provider: "Atlassian",
    lastSync: new Date(Date.now() - 10 * 60 * 1000),
    syncStatus: "success",
    features: [
      "Issue Tracking",
      "Project Management",
      "Test Case Management",
      "Workflow Automation",
    ],
    apiKey: "Basic ...",
    endpoint: "https://your-domain.atlassian.net",
    isConfigured: true,
    isActive: true,
    priority: "high",
  },
];

const categories = [
  { id: "communication", name: "Communication", icon: MessageSquare },
  { id: "development", name: "Development", icon: Code },
];

export const IntegrationsPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedIntegration, setSelectedIntegration] =
    useState<Integration | null>(null);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "connected":
        return "bg-green-900/30 text-green-400 border-green-600/30";
      case "disconnected":
        return "bg-gray-900/30 text-gray-400 border-gray-600/30";
      case "error":
        return "bg-red-900/30 text-red-400 border-red-600/30";
      case "configuring":
        return "bg-yellow-900/30 text-yellow-400 border-yellow-600/30";
      default:
        return "bg-gray-900/30 text-gray-400 border-gray-600/30";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "connected":
        return <CheckCircle className="h-4 w-4 text-green-400" />;
      case "disconnected":
        return <XCircle className="h-4 w-4 text-gray-400" />;
      case "error":
        return <AlertTriangle className="h-4 w-4 text-red-400" />;
      case "configuring":
        return <Clock className="h-4 w-4 text-yellow-400" />;
      default:
        return <XCircle className="h-4 w-4 text-gray-400" />;
    }
  };

  const filteredIntegrations =
    selectedCategory === "all"
      ? integrations
      : integrations.filter(
          (integration) => integration.category === selectedCategory,
        );

  return (
    <div className="flex h-screen bg-gray-900">
      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          "fixed lg:relative z-50 h-full transition-transform duration-300 ease-in-out",
          isSidebarOpen
            ? "translate-x-0"
            : "-translate-x-full lg:translate-x-0",
        )}
      >
        <Sidebar />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col h-full min-h-0 overflow-hidden">
        {/* Top Header */}
        <header className="bg-gray-800 border-b border-gray-700 px-4 lg:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* Mobile Menu Button */}
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden text-gray-300 hover:bg-gray-700"
                onClick={toggleSidebar}
              >
                <Menu className="h-5 w-5" />
              </Button>

              <h1 className="text-xl lg:text-2xl font-bold text-gray-100">
                Integrations
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <Button
                variant="default"
                size="icon"
                className="bg-gray-700 hover:bg-gray-600 text-gray-300"
              >
                <Bell className="h-5 w-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="text-gray-300 hover:bg-gray-700"
              >
                <Settings className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </header>

        {/* Filters */}
        <div className="bg-gray-800 border-b border-gray-700 px-4 lg:px-6 py-3">
          <div className="flex flex-col lg:flex-row lg:items-center space-y-3 lg:space-y-0 lg:space-x-4">
            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <span className="text-sm text-gray-300">Filters:</span>
            </div>

            {/* Category Filter */}
            <Select
              value={selectedCategory}
              onValueChange={setSelectedCategory}
            >
              <SelectTrigger className="w-full lg:w-48 bg-gray-700 border-gray-600 text-gray-100">
                <SelectValue placeholder="Filter by category" />
              </SelectTrigger>
              <SelectContent className="bg-gray-700 border-gray-600">
                <SelectItem
                  value="all"
                  className="text-gray-100 hover:bg-gray-600"
                >
                  All Categories
                </SelectItem>
                {categories.map((category) => {
                  const Icon = category.icon;
                  return (
                    <SelectItem
                      key={category.id}
                      value={category.id}
                      className="text-gray-100 hover:bg-gray-600"
                    >
                      <div className="flex items-center space-x-2">
                        <Icon className="h-4 w-4" />
                        <span>{category.name}</span>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>

            <span className="text-sm text-gray-400">
              {filteredIntegrations.length} of {integrations.length}{" "}
              integrations
            </span>
          </div>
        </div>

        {/* Main Content Area */}
        <main className="flex-1 h-full flex flex-col min-h-0 bg-gray-900">
          {/* Scrollable content area */}
          <div className="flex-1 p-4 lg:p-6 overflow-auto min-h-0">
            {/* Integrations Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredIntegrations.map((integration) => {
                const Icon = integration.icon;
                return (
                  <Card
                    key={integration.id}
                    className="bg-gray-800 border-gray-700 hover:border-gray-600 transition-colors"
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-gray-700 rounded-lg">
                            {integration.logo ? (
                              <img
                                src={integration.logo}
                                alt={`${integration.name} logo`}
                                className="h-5 w-5 object-contain"
                              />
                            ) : (
                              <Icon className="h-5 w-5 text-gray-300" />
                            )}
                          </div>
                          <div>
                            <CardTitle className="text-gray-100 text-lg">
                              {integration.name}
                            </CardTitle>
                            <p className="text-sm text-gray-400">
                              {integration.provider}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {getStatusIcon(integration.status)}
                          <Badge
                            className={`${getStatusColor(integration.status)} border`}
                          >
                            {integration.status}
                          </Badge>
                        </div>
                      </div>
                    </CardHeader>

                    <CardContent className="space-y-4">
                      <p className="text-sm text-gray-300">
                        {integration.description}
                      </p>

                      <div className="flex items-center justify-between pt-2 border-t border-gray-700">
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-gray-600 text-gray-300 hover:bg-gray-700"
                        >
                          <Settings className="h-4 w-4 mr-1" />
                          Configure
                        </Button>

                        <div className="flex items-center space-x-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0 text-gray-400 hover:text-gray-300"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </main>
      </div>

      {/* Configuration Modal */}
      <Dialog open={isConfigModalOpen} onOpenChange={setIsConfigModalOpen}>
        <DialogContent className="bg-gray-800 border-gray-700 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-gray-100 flex items-center space-x-2">
              {selectedIntegration &&
                (selectedIntegration.logo ? (
                  <img
                    src={selectedIntegration.logo}
                    alt={`${selectedIntegration.name} logo`}
                    className="h-5 w-5 object-contain"
                  />
                ) : (
                  <selectedIntegration.icon className="h-5 w-5" />
                ))}
              <span>Configure {selectedIntegration?.name}</span>
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Set up your {selectedIntegration?.name} integration settings
            </DialogDescription>
          </DialogHeader>

          {selectedIntegration && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-300">
                    API Key
                  </label>
                  <input
                    type="password"
                    className="w-full mt-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100"
                    placeholder="Enter API key"
                    defaultValue={selectedIntegration.apiKey}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-300">
                    Endpoint
                  </label>
                  <input
                    type="text"
                    className="w-full mt-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100"
                    placeholder="Enter endpoint URL"
                    defaultValue={selectedIntegration.endpoint}
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-300">
                  Features
                </label>
                <div className="mt-2 space-y-2">
                  {selectedIntegration.features.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`feature-${index}`}
                        className="rounded border-gray-600 bg-gray-700 text-pink-600"
                        defaultChecked
                      />
                      <label
                        htmlFor={`feature-${index}`}
                        className="text-sm text-gray-300"
                      >
                        {feature}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsConfigModalOpen(false)}
            >
              Cancel
            </Button>
            <Button className="bg-pink-400 hover:bg-pink-500">
              Save Configuration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
